import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2, MessageCircle, Sparkles, UserMinus, X } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { formatDateTime } from '@/lib/utils';
import type { Lead } from '@/lib/types';
import type { WhatsAppDeLead } from '@/lib/comunicaciones';
import { MensajesWhatsApp, RedactarWhatsApp } from '@/components/comunicaciones/HiloWhatsApp';
import ElegirPlantilla from '@/components/comunicaciones/ElegirPlantilla';
import ProcesarConversacion from './ProcesarConversacion';

/**
 * WhatsApp con un lead, en un panel a un lado de su ficha (a pantalla completa
 * en el móvil). Para empezar, o para retomar pasadas 24 h desde su último
 * mensaje, WhatsApp solo deja mandar una plantilla «para leads»; en cuanto
 * contesta, se habla libremente.
 *
 * Lo que conteste entra aquí, en Comunicaciones → Conversaciones y en el tema
 * Leads de Telegram (ver services/whatsapp/leads.ts en el servidor).
 *
 * Los mensajes no se apuntan uno a uno en la Actividad del lead: «Procesar»
 * resume lo nuevo y lo apunta cuando se acepta (ProcesarConversacion).
 */
export default function WhatsAppLead({ lead, onClose, onProcesada }: { lead: Lead; onClose: () => void; onProcesada?: () => void }) {
  const [datos, setDatos] = useState<WhatsAppDeLead | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [procesando, setProcesando] = useState(false);
  const [apuntada, setApuntada] = useState(false);
  const nombre = [lead.firstName, lead.lastName].map(x => (x || '').trim()).filter(Boolean).join(' ') || 'Lead';

  const cargar = useCallback(async () => {
    try {
      const d = await api.getWhatsAppDeLead(lead.id);
      setDatos(d);
      setError(null);
      if (d.telefono && d.conversacion && d.conversacion.sinLeer > 0) {
        await api.marcarLeidaWhatsApp(d.telefono);
        window.dispatchEvent(new Event('sereno:contadores'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar');
    }
  }, [lead.id]);

  // Mientras está abierto se refresca solo: es un chat, el lead contesta mientras lo miras
  useEffect(() => {
    void cargar();
    const t = setInterval(() => { void cargar(); }, 15_000);
    return () => clearInterval(t);
  }, [cargar]);

  // Lo que queda por procesar: lo posterior al último proceso aceptado
  const hastaProcesada = datos?.conversacion?.procesada?.hasta;
  const sinProcesar = (datos?.mensajes || []).filter(m => !hastaProcesada || m.en > hastaProcesada).length;

  useEffect(() => {
    if (!apuntada) return;
    const t = setTimeout(() => setApuntada(false), 6000);
    return () => clearTimeout(t);
  }, [apuntada]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside role="dialog" aria-label={`WhatsApp con ${nombre}`} className="relative h-full w-full sm:w-[440px] bg-white shadow-2xl flex flex-col">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
          <MessageCircle size={18} className="text-emerald-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sereno-dark truncate">{nombre}</div>
            <div className="text-xs text-sereno-dark/40 truncate">{datos?.telefono || lead.phone || 'Sin teléfono'} · WhatsApp</div>
          </div>
          {datos?.telefono && datos.mensajes.length > 0 && !procesando && (
            <button
              type="button"
              onClick={() => { setApuntada(false); setProcesando(true); }}
              disabled={sinProcesar === 0}
              title={sinProcesar === 0 ? 'No hay mensajes nuevos desde el último proceso' : 'Resumir lo nuevo y apuntarlo en la Actividad del lead (te enseña la propuesta antes)'}
              className="btn-ghost border border-gray-200 text-xs px-2.5 py-1 flex items-center gap-1 disabled:opacity-40"
            >
              <Sparkles size={13} /> Procesar{sinProcesar > 0 && <span className="text-sereno-dark/40">· {sinProcesar}</span>}
            </button>
          )}
          <button type="button" onClick={onClose} aria-label="Cerrar" className="p-1.5 -mr-1.5 text-sereno-dark/50 hover:text-sereno-dark"><X size={18} /></button>
        </div>
        {apuntada && (
          <div className="px-4 py-2 text-xs bg-emerald-50 text-emerald-800 flex items-center gap-1.5">
            <CheckCircle2 size={13} /> Apuntado en la Actividad del lead.
          </div>
        )}

        {!datos ? (
          <div className="flex-1 flex items-center justify-center px-6">
            {error ? <p className="text-sm text-red-600">{error}</p> : <Loader2 size={20} className="animate-spin text-sereno-dark/30" />}
          </div>
        ) : !datos.telefono ? (
          <div className="flex-1 p-6 space-y-2 text-sm">
            <p className="flex items-start gap-2 text-sereno-dark/80">
              <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
              <span>El teléfono de la ficha no vale para WhatsApp{datos.telefonoOriginal ? <>: «{datos.telefonoOriginal}»</> : ' (no tiene)'}.</span>
            </p>
            <p className="text-sereno-dark/50">Corrígelo en la ficha. Si no es español, ponlo con su prefijo (+44…, +49…).</p>
          </div>
        ) : procesando ? (
          <ProcesarConversacion
            lead={lead}
            onCancelar={() => setProcesando(false)}
            onAceptada={() => { setProcesando(false); setApuntada(true); void cargar(); onProcesada?.(); }}
          />
        ) : (
          <>
            {datos.baja && (
              <div className="px-4 py-2 text-xs bg-amber-50 text-amber-800 flex items-center gap-1.5">
                <UserMinus size={13} /> Pidió no recibir más mensajes por WhatsApp el {formatDateTime(datos.baja)}.
              </div>
            )}
            <MensajesWhatsApp
              mensajes={datos.mensajes}
              className="flex-1"
              vacio={<p className="text-center text-sm mt-10" style={{ color: '#667781' }}>Todavía no habéis hablado por WhatsApp.<br />Para empezar, mándale una plantilla.</p>}
            />
            <div className="border-t border-gray-100 p-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
              {error && <div className="text-xs text-red-600 mb-2">{error}</div>}
              {datos.baja ? (
                <p className="text-xs text-sereno-dark/50">No se le puede escribir: pidió no recibir más mensajes.</p>
              ) : datos.ventana.abierta ? (
                <RedactarWhatsApp telefono={datos.telefono} hasta={datos.ventana.hasta} onEnviado={cargar} />
              ) : (
                <EmpezarConPlantilla lead={lead} retomar={datos.mensajes.length > 0} onEnviada={cargar} />
              )}
            </div>
          </>
        )}
      </aside>
    </div>,
    document.body,
  );
}

/**
 * Elegir la plantilla «para leads» y mandarla. {{1}} del cuerpo lleva el
 * nombre del lead; si no tiene nombre, o hay más huecos, se escriben aquí.
 */
function EmpezarConPlantilla({ lead, retomar, onEnviada }: { lead: Lead; retomar: boolean; onEnviada: () => Promise<void> }) {
  const plantillas = useApi(() => api.getPlantillasWhatsApp(), []);
  const disponibles = useMemo(
    () => (plantillas.data || []).filter(p => p.delCrm && p.uso === 'leads' && p.aprobada && !p.huecos.some(h => h.imagen)),
    [plantillas.data],
  );

  return (
    <div className="space-y-2">
      <p className="text-xs text-sereno-dark/50">
        {retomar
          ? 'Han pasado más de 24 h desde su último mensaje: para retomar la conversación, WhatsApp solo deja mandar una plantilla.'
          : 'Para empezar, WhatsApp solo deja mandar una plantilla aprobada. Cuando conteste, podréis hablar libremente.'}
      </p>
      {plantillas.loading && !plantillas.data ? (
        <Loader2 size={16} className="animate-spin text-sereno-dark/30" />
      ) : plantillas.error ? (
        <p className="text-xs text-red-600">No se pudieron leer las plantillas: {plantillas.error}</p>
      ) : disponibles.length === 0 ? (
        <p className="text-xs text-amber-700">
          No hay ninguna plantilla «para leads» aprobada.{' '}
          <Link to="/comunicaciones/plantillas" className="underline">Créala en Comunicaciones → Plantillas</Link> eligiendo «Para leads».
        </p>
      ) : (
        <ElegirPlantilla
          disponibles={disponibles}
          nombrePila={lead.firstName || ''}
          idioma={lead.preferredLanguage || 'es'}
          onEnviar={async (plantilla, parametros) => { await api.enviarPlantillaALead(lead.id, plantilla, parametros); await onEnviada(); }}
        />
      )}
    </div>
  );
}
